import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";
import crypto from "crypto"; // NOSONAR
import { FieldErrors } from "react-hook-form";
import { VALIDATION_MESSAGE_TYPES } from "@/constants/constants";

export const addIfDefined = <T extends string, U>(key: T, value: U) => (value ? { [key]: value } : {});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Remove undefined values from an object
export function removeUndefinedValues<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as Partial<T>;
}

// Random string generator function
export const generateRandomId = (): string => {
  return uuidv4();
};

// Get random number
export const getRandomNumber = () => {
  const buffer = crypto.randomBytes(4);
  const randomNumber = buffer.readUInt32BE(0) / (0xffffffff + 1);
  return randomNumber;
};

export const generateRandomNumberInRange = (min: number, max: number): number => {
  return Math.floor(getRandomNumber() * (max - min + 1)) + min;
};

export const delay = (ms: number = 0) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, ms);
  });
};

// Generic function to merge arrays avoiding duplicates
export const mergeUnique = <T>(existing: T[], incoming: T[], compareFn: (existingItem: T, incomingItem: T) => boolean): T[] => {
  if (!existing) existing = [];
  if (!incoming) incoming = [];
  return [...existing, ...incoming.filter((item) => !existing.some((existingItem) => compareFn(existingItem, item)))];
};

export const getCombinedErrorMessage = (errors: FieldErrors): string => {
  const messages: string[] = [];
  for (const key in errors) {
    const error = errors[key];

    if (!error) continue;

    if (typeof error.message === "string") {
      messages.push(error.message);
    } else if (typeof error === "object" && "message" in error) {
      const msg = (error as { message: unknown }).message;
      if (typeof msg === "string") {
        messages.push(msg);
      } else if (typeof msg === "object" && msg !== null) {
        messages.push(getCombinedErrorMessage(msg as FieldErrors));
      }
    } else if (typeof error === "object") {
      messages.push(getCombinedErrorMessage(error as FieldErrors));
    }
  }
  return messages.join(" | ");
};

/**
 * Recursively finds all string values in a nested object or array.
 * Unused in the app.
 */
export const findStringsInObject = (input: object): Set<string> => {
  const foundStrings = new Set<string>();

  const traverse = (value: unknown) => {
    if (typeof value === "string") {
      foundStrings.add(value);
    } else if (Array.isArray(value)) {
      value.forEach(traverse);
    } else if (typeof value === "object" && value !== null) {
      Object.values(value).forEach(traverse);
    }
  };
  traverse(input);
  return foundStrings;
};

export function createNumberArray(length: number, start = 0): number[] {
  return Array.from({ length }, (_, i) => i + start);
}

export const getRAIStatusFlags = (messageType?: string) => {
  return {
    isRAISuccess: messageType === VALIDATION_MESSAGE_TYPES.RAI_SUCCESS,
    isRAIError: messageType === VALIDATION_MESSAGE_TYPES.RAI_ERROR,
  };
};
